import { Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { VFC } from 'react';
import styled from 'styled-components';

import RulesSection from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/RulesSection';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowRunStepConditional } from 'dashboard/models/workflow-step';
import { WorkflowConditional } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { JsonSchema } from 'lib/json-schema';
import { TokensToNode } from 'lib/workflows';

const FIRST_ROUTE_HELP_TEXT = 'This route is always evaluated.';
const OTHER_ROUTE_HELP_TEXT = 'Only evaluated if none of the prior conditions are met.';

const HeadingContainer = styled.div<{
  fullOpacity: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  opacity: ${({ fullOpacity }) => (fullOpacity ? '1' : '0.4')};
`;

const RouteHeading = styled(Heading)`
  padding-right: ${Spacing[2]};
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 0;
  flex: 1;
`;

const RouteHeadingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const RouteName = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  word-wrap: pre-wrap;
  text-overflow: ellipsis;
  margin-right: 0;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface ConditionalConfigurationProps {
  conditional: WorkflowConditional;
  editable: boolean;
  errors: WorkflowError[];
  headingText: string;
  jsonSchema: JsonSchema;
  routeIndex?: number;
  tokensToDisplayValue: Record<string, string>;
  tokensToSteps: TokensToNode;
  usableObjects: UsableObjectConfig[];
  workflowRunStep?: WorkflowRunStepConditional;
  onEditClick: () => void;
  deleteRoute?: () => void;
}

const ConditionalConfiguration: VFC<ConditionalConfigurationProps> = ({
  conditional,
  editable,
  errors,
  headingText,
  jsonSchema,
  routeIndex = null,
  tokensToDisplayValue,
  tokensToSteps,
  usableObjects,
  workflowRunStep = null,
  deleteRoute,
  onEditClick,
}) => {
  const { name, rules } = conditional;

  const chosenRoute = workflowRunStep?.attributes?.['chosen-conditional-index'];
  const showMatch = chosenRoute != null && routeIndex != null && chosenRoute === routeIndex;
  const { renderErrorMessageFor } = useWorkflowError(errors);

  return (
    <>
      <HeadingContainer fullOpacity={showMatch || !workflowRunStep}>
        <RouteHeadingContainer>
          <RouteHeading size='sm'>{headingText}</RouteHeading>
          {showMatch && <Sticker variant='primary'>Match</Sticker>}
        </RouteHeadingContainer>
        {editable && (
          <HStack>
            <Button
              variant='basic'
              width='icon'
              type='button'
              onClick={onEditClick}
              id={`conditional-configuration__open-edit-panel-${routeIndex}`}
              aria-label='edit conditional'
              data-test={`conditional-configuration__open-edit-panel-${routeIndex}`}
            >
              <EditIcon />
            </Button>
            {deleteRoute && (
              <Button
                variant='danger'
                width='icon'
                type='button'
                onClick={deleteRoute}
                id='conditional-configuration__delete-route'
                aria-label='delete conditional'
                data-test='conditional-configuration__delete-route'
              >
                <TrashIcon />
              </Button>
            )}
          </HStack>
        )}
      </HeadingContainer>
      {name !== '' && <RouteName>{name}</RouteName>}
      {routeIndex != null && (
        <>
          <Spacer height={1} />
          <Paragraph size='sm'>
            {routeIndex === 0 ? FIRST_ROUTE_HELP_TEXT : OTHER_ROUTE_HELP_TEXT}
          </Paragraph>
        </>
      )}
      <Spacer height={1} />
      <RulesSection
        editable={editable}
        errors={errors}
        rules={rules}
        tokensToSteps={tokensToSteps}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        usableObjects={usableObjects}
        workflowRunStep={workflowRunStep ?? undefined}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        routeIndex={routeIndex}
        rootPathForField={routeIndex != null ? `conditionals[${routeIndex}].` : `conditional.`}
      />
      {renderErrorMessageFor([
        routeIndex != null ? `conditionals[${routeIndex}].rules.or` : 'conditional.rules.or',
        `branches[${routeIndex}]`,
      ])}
    </>
  );
};

export default ConditionalConfiguration;
